import * as Phaser from 'phaser';
import { EventBus, IEvent } from '../core/EventBus';
import { AudioManager } from '../managers/AudioManager';
import { ParticleManager } from '../managers/ParticleManager';
import { createLogger } from '../utils/Logger';
import { RuntimeTestingModeState } from '../config/RuntimeTestingMode';
import { GameSettingsContent } from '../data';
import { COMBAT_VISUAL_LIMITS } from '../config/GameConfig';
import { LocalizationManager } from '../managers/LocalizationManager';
import {
  isEnemyHitFeedbackPayload,
  isEnemyKilledFeedbackPayload,
  isBossKilledFeedbackPayload,
  isPlayerDeadFeedbackPayload,
  isPlayerHitFeedbackPayload
} from './CombatFeedbackTypes';

export class CombatFeedbackSystem {
  private graphics?: Phaser.GameObjects.Graphics;
  private shadows?: Phaser.GameObjects.Graphics;
  private shadowCount = 0;
  private readonly shadowPositions = new WeakMap<Phaser.Physics.Arcade.Sprite, readonly number[]>();
  private readonly projectiles = new Map<Phaser.Physics.Arcade.Sprite, { generation: unknown; x: number; y: number; age: number }>();
  private segments: Array<{ x: number; y: number; toX: number; toY: number; ttl: number; color: number; chain: boolean }> = [];
  private deaths: Array<{ image: Phaser.GameObjects.Image; ttl: number }> = [];
  private freeDeaths: Phaser.GameObjects.Image[] = [];
  private counters = { attacks: 0, deaths: 0, pickups: 0, suppressed: 0, burn: 0, slow: 0 };
  private reduceShake = false;
  private reduceFlashes = false;
  private playerTint?: { player: Phaser.Physics.Arcade.Sprite; colors: [number, number, number, number]; timer?: Phaser.Time.TimerEvent };
  private readonly killedBosses = new Set<string>();
  private waveLabel?: Phaser.GameObjects.Text;
  private waveLabelSeconds = 0;
  private readonly logger = createLogger('CombatFeedbackSystem');
  private readonly unsubscribers: Array<() => void> = [];
  private lastDispatch: {
    effectKey: string;
    particleKey?: string;
    cameraSuppressed: boolean;
    audioKey?: string;
  } | null = null;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly eventBus: EventBus,
    private readonly particleManager: ParticleManager,
    private readonly audioManager: AudioManager,
    private runtimeTestingMode: RuntimeTestingModeState,
    private readonly combatFeedback: GameSettingsContent['combatFeedback']
  ) {
    this.bind();
    this.logger.debug('visuals:initialized', { limits: COMBAT_VISUAL_LIMITS, geometry: 'physical-projectiles', characterPhysics: 'separate-image' });
  }

  public setRuntimeTestingMode(state: RuntimeTestingModeState): void {
    this.runtimeTestingMode = state;
    this.stopSuppressedEffects();
    this.logger.info('setRuntimeTestingMode:applied', state);
  }

  public setVisualSettings(gameplay: Record<string, boolean | number | string>): void {
    this.reduceShake = gameplay.reduceShake === true;
    this.reduceFlashes = gameplay.reduceFlashes === true;
    this.particleManager.setReducedFlashes?.(this.reduceFlashes);
    this.stopSuppressedEffects();
    this.logger.debug('visual-settings:applied', { reduceShake: this.reduceShake, reduceFlashes: this.reduceFlashes, testingSuppression: this.runtimeTestingMode.enabled && this.runtimeTestingMode.suppressCameraFeedback });
  }

  private stopSuppressedEffects(): void {
    const suppressed = this.runtimeTestingMode.enabled && this.runtimeTestingMode.suppressCameraFeedback;
    if (suppressed || this.reduceShake) this.scene.cameras.main.shakeEffect?.reset?.();
    if (suppressed || this.reduceFlashes) this.scene.cameras.main.flashEffect?.reset?.();
    if (this.reduceFlashes) this.clearPlayerTint();
  }

  private clearPlayerTint(): void {
    if (!this.playerTint) return;
    this.playerTint.timer?.remove(false);
    this.playerTint.player.setTint(...this.playerTint.colors);
    this.playerTint = undefined;
  }

  public dispose(): void {
    this.clearPlayerTint();
    this.unsubscribers.splice(0).forEach(unsubscribe => unsubscribe());
    this.graphics?.destroy();
    this.shadows?.destroy();
    this.waveLabel?.destroy();
    [...this.deaths.map(entry => entry.image), ...this.freeDeaths].forEach(image => image.destroy());
    this.deaths = []; this.freeDeaths = []; this.segments = []; this.projectiles.clear();
    this.logger.info('dispose:complete');
  }


  public getDiagnosticsSnapshot() {
    return { attackBounds: [...this.projectiles.keys()].filter(projectile => projectile.active).map(projectile => ({ weaponId: projectile.getData('weaponId'), x: projectile.x, y: projectile.y, width: projectile.displayWidth, height: projectile.displayHeight, rotation: projectile.rotation, areaRadius: projectile.getData('areaRadius') })), chainLinks: this.segments.filter(segment => segment.chain).map(({ x,y,toX,toY }) => ({ x,y,toX,toY })), ...this.counters, trackedProjectiles: this.projectiles.size, segments: this.segments.length, deathImages: this.deaths.length, pooledDeathImages: this.freeDeaths.length, reduceShake: this.reduceShake, reduceFlashes: this.reduceFlashes };
  }

  private addSegment(x: number, y: number, toX: number, toY: number, color: number, chain = false): void {
    if (this.segments.length >= COMBAT_VISUAL_LIMITS.segments) { this.counters.suppressed++; return; }
    this.segments.push({ x, y, toX, toY, color, chain, ttl: COMBAT_VISUAL_LIMITS.segmentSeconds });
  }

  private snapshotDeath(sprite: Phaser.Physics.Arcade.Sprite, x: number, y: number): void {
    this.counters.deaths++;
    if (!sprite.texture || !this.scene.add?.image || this.deaths.length >= COMBAT_VISUAL_LIMITS.deathImages) return;
    const image = this.freeDeaths.pop() ?? this.scene.add.image(x, y, sprite.texture.key);
    image.setTexture(sprite.texture.key, sprite.frame.name).setPosition(x, y).setVisible(true)
      .setDisplaySize(sprite.displayWidth, sprite.displayHeight).setAlpha(0.7).setDepth(sprite.depth)
      .setTint(sprite.tintTopLeft, sprite.tintTopRight, sprite.tintBottomLeft, sprite.tintBottomRight);
    this.deaths.push({ image, ttl: COMBAT_VISUAL_LIMITS.deathSeconds });
  }

  private shadowMoved(sprite: Phaser.Physics.Arcade.Sprite): boolean {
    const previous = this.shadowPositions.get(sprite);
    if (previous && previous[0] === sprite.x && previous[1] === sprite.y && previous[2] === sprite.displayWidth && previous[3] === sprite.displayHeight) return false;
    this.shadowPositions.set(sprite, [sprite.x, sprite.y, sprite.displayWidth, sprite.displayHeight]);
    return true;
  }

  public update(dt: number, enemies: readonly Phaser.Physics.Arcade.Sprite[] = [], player?: Phaser.Physics.Arcade.Sprite): void {
    this.waveLabelSeconds = Math.max(0, this.waveLabelSeconds - dt);
    this.waveLabel?.setPosition(this.scene.scale.width / 2, 106).setVisible(this.waveLabelSeconds > 0);
    if (!this.scene.add?.graphics) return;
    this.graphics ??= this.scene.add.graphics().setDepth(18);
    this.shadows ??= this.scene.add.graphics().setDepth(-1);
    let shadowsChanged = false;
    let shadowCount = 0;
    const graphics = this.graphics.clear();
    if (player?.active) {
      shadowsChanged = this.shadowMoved(player);
      shadowCount++;
      const y = player.y + player.displayHeight * 0.55;
      graphics.lineStyle(3, 0xc9ec99, 0.9).lineBetween(player.x - 9, y, player.x, y + 5).lineBetween(player.x, y + 5, player.x + 9, y);
    }
    this.counters.burn = 0; this.counters.slow = 0;
    for (const enemy of enemies) {
      if (!enemy.active) continue;
      if (this.shadowMoved(enemy)) shadowsChanged = true;
      shadowCount++;
      const radius = enemy.displayWidth * 0.45;
      if (enemy.getData('burn')) {
        this.counters.burn++;
        graphics.lineStyle(2, 0xef913d, 0.85).strokeTriangle(enemy.x - 6, enemy.y - radius, enemy.x + 6, enemy.y - radius, enemy.x, enemy.y - radius - 12);
      }
      if (enemy.getData('slow')) {
        this.counters.slow++;
        graphics.lineStyle(2, 0x82c9e7, 0.85).strokeCircle(enemy.x, enemy.y + radius * 0.6, radius * 0.8);
      }

    }
    if (shadowsChanged || shadowCount !== this.shadowCount) {
      this.shadows.clear().fillStyle(0x071711, 0.35);
      for (const sprite of player ? [...enemies, player] : enemies) {
        if (sprite.active) this.shadows.fillEllipse(sprite.x, sprite.y + sprite.displayHeight * 0.37, sprite.displayWidth * 0.65, sprite.displayHeight * 0.17, 8);
      }
      this.shadowCount = shadowCount;
    }
    for (const [projectile, state] of this.projectiles) {
      if (!projectile.active || projectile.getData('visualGeneration') !== state.generation || state.age > 8) { this.projectiles.delete(projectile); continue; }
      if (state.age === 0) player?.getData('characterVisual')?.react?.();
      state.age += dt;
      const type = projectile.getData('weaponType');
      const id = projectile.getData('weaponId');
      const color = id === 'death_spiral' ? 0xc394ed : id === 'holy_wand' ? 0xecc778 : projectile.getData('visualReturning') ? 0x9eddc5 : 0x85bee6;
      if (type === 'projectile' || type === 'boomerang' || type === 'orbit') {
        if (Math.hypot(projectile.x - state.x, projectile.y - state.y) > 3) this.addSegment(state.x, state.y, projectile.x, projectile.y, color);
        if (type === 'boomerang' || id === 'death_spiral') projectile.setRotation(state.age * 18);
        if (type === 'projectile') projectile.setRotation(Math.atan2(projectile.y-state.y, projectile.x-state.x));
        graphics.lineStyle(id === 'holy_wand' ? 3 : 1, color, 0.8).strokeCircle(projectile.x, projectile.y, Math.max(3, projectile.displayWidth * 0.45));
        if (id === 'death_spiral') {
          const dx = Math.cos(state.age * 18) * 10, dy = Math.sin(state.age * 18) * 10;
          graphics.lineStyle(3, color, 0.85).lineBetween(projectile.x-dx,projectile.y-dy,projectile.x+dx,projectile.y+dy).lineBetween(projectile.x-dy,projectile.y+dx,projectile.x+dy,projectile.y-dx);
        }
      } else if (type === 'beam') {
        const dx = Math.cos(projectile.rotation) * projectile.displayWidth / 2, dy = Math.sin(projectile.rotation) * projectile.displayWidth / 2;
        graphics.lineStyle(2, 0xc5e5ed, this.reduceFlashes ? 0.4 : 0.8).lineBetween(projectile.x-dx,projectile.y-dy,projectile.x+dx,projectile.y+dy);
      }
      state.x = projectile.x; state.y = projectile.y;
    }
    this.segments = this.segments.filter(segment => {
      segment.ttl -= dt;
      if (segment.ttl <= 0) return false;
      graphics.lineStyle(2, segment.color, segment.ttl / COMBAT_VISUAL_LIMITS.segmentSeconds * (this.reduceFlashes ? 0.3 : 0.65));
      graphics.lineBetween(segment.x, segment.y, segment.toX, segment.toY);
      return true;
    });
    this.deaths = this.deaths.filter(entry => {
      entry.ttl -= dt;
      if (entry.ttl <= 0) { entry.image.setVisible(false); this.freeDeaths.push(entry.image); return false; }
      entry.image.setAlpha(entry.ttl / 0.3 * 0.7).setRotation((0.3-entry.ttl)*1.4);
      entry.image.y -= dt * 12;
      return true;
    });
  }

  public triggerDeterministicSmoke(): {
    effectKey: string;
    particleKey?: string;
    cameraSuppressed: boolean;
    audioKey?: string;
  } {
    const x = this.scene.cameras.main.scrollX + this.scene.cameras.main.width / 2;
    const y = this.scene.cameras.main.scrollY + this.scene.cameras.main.height / 2;
    this.playParticleBurst('hit', this.combatFeedback.hit, x, y);
    const cameraSuppressed = this.playCameraEffect(this.combatFeedback.playerHit);
    const audioKey = this.combatFeedback.hit?.sound?.key;
    this.audioManager.playFeedbackCue(this.combatFeedback.hit?.sound);
    this.lastDispatch = {
      effectKey: 'hit',
      particleKey: 'hit',
      cameraSuppressed,
      audioKey
    };
    this.logger.debug('triggerDeterministicSmoke:complete', this.lastDispatch);
    return this.lastDispatch;
  }

  private bind(): void {
    this.subscribe('wave:started', event => {
      if (!Number.isInteger(event.data?.waveNumber) || !this.scene.add?.text) return;
      this.waveLabel ??= this.scene.add.text(0, 0, '', { fontSize: '22px', color: '#ecc778', backgroundColor: '#101f1c', padding: { x: 12, y: 6 } }).setOrigin(0.5).setScrollFactor(0).setDepth(80);
      this.waveLabel.setText(LocalizationManager.getInstance().getText('ui.wave', [event.data.waveNumber]));
      this.waveLabelSeconds = 1.4;
      this.logger.debug('wave:signal', { waveNumber: event.data.waveNumber, reduceFlashes: this.reduceFlashes });
    });
    this.subscribe('weapon:fired', event => {
      const projectile = event.data?.projectile as Phaser.Physics.Arcade.Sprite | undefined;
      if (!projectile?.active) return;
      this.counters.attacks++;
      if (this.projectiles.size < COMBAT_VISUAL_LIMITS.trackedProjectiles || this.projectiles.has(projectile)) this.projectiles.set(projectile, { generation: projectile.getData('visualGeneration'), x: projectile.x, y: projectile.y, age: 0 });
    });
    this.subscribe('combat:chain', event => {
      const data = event.data;
      if (![data?.x, data?.y, data?.toX, data?.toY].every(Number.isFinite)) return;
      this.addSegment(data.x, data.y, data.toX, data.toY, 0x99ddff, true);
    });
    this.subscribe('loot:collected', event => {
      if (!Number.isFinite(event.data?.x) || !Number.isFinite(event.data?.y)) return;
      this.counters.pickups++;
      this.particleManager.playEffect('slow', event.data.x, event.data.y, 4);
    });
    this.subscribe('enemy:hit', event => this.onEnemyHit(event));
    this.subscribe('enemy:killed', event => this.onEnemyKilled(event));
    this.subscribe('player:hit', event => this.onPlayerHit(event));
    this.subscribe('player:dead', event => this.onPlayerDead(event));
    this.subscribe('boss:spawned', () => this.onBossSpawned());
    this.subscribe('boss:killed', event => this.onBossKilled(event));
    this.subscribe('level:up', () => {
      this.playParticleBurst('levelup', this.combatFeedback.levelUp);
      this.audioManager.playFeedbackCue(this.combatFeedback.levelUp?.sound);
    });
    this.subscribe('chest:opened', () => this.playParticleBurst('chestReward', this.combatFeedback.chestReward));

    this.logger.info('bind:complete', { subscriptions: this.unsubscribers.length });
  }

  private subscribe(type: string, listener: (event: IEvent) => void): void {
    this.eventBus.on(type, listener);
    this.unsubscribers.push(() => this.eventBus.off(type, listener));
  }

  private onEnemyHit(event: IEvent): void {
    if (!isEnemyHitFeedbackPayload(event.data)) {
      this.logger.warn('onEnemyHit:malformed-payload', { eventType: event.type, data: event.data });
      return;
    }

    event.data.enemy.getData?.('characterVisual')?.react?.();
    if (event.data.effects?.burn) this.playParticleBurst('burn', this.combatFeedback.burn, event.data.x, event.data.y);
    if (event.data.effects?.slow) this.playParticleBurst('slow', this.combatFeedback.slow, event.data.x, event.data.y);
    if (event.data.critical) {
      this.playParticleBurst('crit', this.combatFeedback.crit, event.data.x, event.data.y);
      this.audioManager.playFeedbackCue(this.combatFeedback.crit?.sound);
      this.lastDispatch = { effectKey: 'crit', particleKey: 'crit', cameraSuppressed: false, audioKey: this.combatFeedback.crit?.sound?.key };
      return;
    }

    if (event.data.effects?.burn) {
      this.audioManager.playFeedbackCue(this.combatFeedback.burn?.sound);
      this.lastDispatch = { effectKey: 'burn', particleKey: 'burn', cameraSuppressed: false, audioKey: this.combatFeedback.burn?.sound?.key };
      return;
    }

    if (event.data.effects?.slow) {
      this.audioManager.playFeedbackCue(this.combatFeedback.slow?.sound);
      this.lastDispatch = { effectKey: 'slow', particleKey: 'slow', cameraSuppressed: false, audioKey: this.combatFeedback.slow?.sound?.key };
      return;
    }

    if (event.data.kind === 'chain') {
      this.playParticleBurst('chain', this.combatFeedback.chain, event.data.x, event.data.y);
      this.audioManager.playFeedbackCue(this.combatFeedback.chain?.sound);
      this.lastDispatch = { effectKey: 'chain', particleKey: 'chain', cameraSuppressed: false, audioKey: this.combatFeedback.chain?.sound?.key };
      return;
    }

    if (event.data.kind === 'area') {
      this.playParticleBurst('area', this.combatFeedback.area, event.data.x, event.data.y);
      this.audioManager.playFeedbackCue(this.combatFeedback.area?.sound);
      this.lastDispatch = { effectKey: 'area', particleKey: 'area', cameraSuppressed: false, audioKey: this.combatFeedback.area?.sound?.key };
      return;
    }

    this.playParticleBurst('hit', this.combatFeedback.hit, event.data.x, event.data.y);
    this.audioManager.playFeedbackCue(this.combatFeedback.hit?.sound);
    this.lastDispatch = { effectKey: 'hit', particleKey: 'hit', cameraSuppressed: false, audioKey: this.combatFeedback.hit?.sound?.key };
  }

  private onEnemyKilled(event: IEvent): void {
    if (!isEnemyKilledFeedbackPayload(event.data)) {
      this.logger.warn('onEnemyKilled:malformed-payload', { eventType: event.type, data: event.data });
      return;
    }

    this.snapshotDeath(event.data.enemy, event.data.x, event.data.y);
    event.data.enemy.getData?.('characterVisual')?.reset?.();
    const isBoss = event.data.id === 'boss' || event.data.enemy.getData('isBoss') === true;
    if (isBoss) {
      this.logger.debug('onEnemyKilled:boss-feedback-deferred-to-boss-killed', { id: event.data.id });
      return;
    }

    this.playParticleBurst('death', this.combatFeedback.enemyDeath, event.data.x, event.data.y);
    this.audioManager.playFeedbackCue(this.combatFeedback.enemyDeath?.sound);
    this.lastDispatch = { effectKey: 'enemyDeath', particleKey: 'death', cameraSuppressed: false, audioKey: this.combatFeedback.enemyDeath?.sound?.key };
  }

  private onPlayerHit(event: IEvent): void {
    if (!isPlayerHitFeedbackPayload(event.data)) {
      this.logger.warn('onPlayerHit:malformed-payload', { eventType: event.type, data: event.data });
      return;
    }

    this.playPlayerTint(event.data.player, this.combatFeedback.playerHit);
    const cameraSuppressed = this.playCameraEffect(this.combatFeedback.playerHit);
    this.audioManager.playFeedbackCue(this.combatFeedback.playerHit?.sound);
    this.lastDispatch = { effectKey: 'playerHit', cameraSuppressed, audioKey: this.combatFeedback.playerHit?.sound?.key };
  }

  private onBossSpawned(): void {
    const cameraSuppressed = this.playCameraEffect(this.combatFeedback.bossWarning);
    this.audioManager.playFeedbackCue(this.combatFeedback.bossWarning?.sound);
    this.lastDispatch = {
      effectKey: 'bossWarning',
      cameraSuppressed,
      audioKey: this.combatFeedback.bossWarning?.sound?.key
    };
  }

  private onPlayerDead(event: IEvent): void {
    if (!isPlayerDeadFeedbackPayload(event.data)) {
      this.logger.warn('onPlayerDead:malformed-payload', { eventType: event.type, data: event.data });
      return;
    }

    const cameraSuppressed = this.playCameraEffect(this.combatFeedback.playerHit);
    this.audioManager.playFeedbackCue(this.combatFeedback.playerHit?.sound);
    this.lastDispatch = { effectKey: 'playerDead', cameraSuppressed, audioKey: this.combatFeedback.playerHit?.sound?.key };
  }

  private onBossKilled(event: IEvent): void {
    if (!isBossKilledFeedbackPayload(event.data)) {
      this.logger.warn('onBossKilled:malformed-payload', { eventType: event.type, data: event.data });
      return;
    }

    const id = event.data?.bossEncounter?.encounterId;
    if (id && this.killedBosses.has(id)) return;
    if (id) this.killedBosses.add(id);

    this.playParticleBurst('bossDeath', this.combatFeedback.bossDeath, event.data?.x, event.data?.y);
    const cameraSuppressed = this.playCameraEffect(this.combatFeedback.bossDeath);
    this.audioManager.playFeedbackCue(this.combatFeedback.bossDeath?.sound);
    this.lastDispatch = { effectKey: 'bossDeath', particleKey: 'bossDeath', cameraSuppressed, audioKey: this.combatFeedback.bossDeath?.sound?.key };
  }

  public playRewardFeedback(effectKey: 'evolutionReveal' | 'chestReward'): void {
    const effect = this.combatFeedback[effectKey];
    this.playParticleBurst('chestReward', effect);
    this.audioManager.playFeedbackCue(effect?.sound);
    this.lastDispatch = { effectKey, particleKey: 'chestReward', cameraSuppressed: false, audioKey: effect?.sound?.key };
    this.logger.debug('reward:feedback-applied', this.lastDispatch);
  }

  private playParticleBurst(
    key: string,
    effect: GameSettingsContent['combatFeedback'][keyof GameSettingsContent['combatFeedback']] | undefined,
    x?: number,
    y?: number
  ): void {
    const worldX = x ?? (this.scene.cameras.main.scrollX + this.scene.cameras.main.width / 2);
    const worldY = y ?? (this.scene.cameras.main.scrollY + this.scene.cameras.main.height / 2);
    const count = Math.max(0, Math.round(effect?.particleBurstCount ?? 0));

    this.particleManager.playEffect(key, worldX, worldY, count);
  }

  private playCameraEffect(effect: GameSettingsContent['combatFeedback'][keyof GameSettingsContent['combatFeedback']] | undefined): boolean {
    const shakeDuration = Math.max(0, Number(effect?.shakeDurationMs ?? 0));
    const shakeIntensity = Math.max(0, Number(effect?.shakeIntensity ?? 0));

    if (this.runtimeTestingMode.enabled && this.runtimeTestingMode.suppressCameraFeedback) {
      this.logger.info('playCameraEffect:suppressed-by-testing-mode', { shakeDuration, shakeIntensity });
      return true;
    }

    if (!this.reduceShake && shakeDuration > 0 && shakeIntensity > 0) {
      this.scene.cameras.main.shake(shakeDuration, shakeIntensity);
      this.logger.debug('playCameraEffect:shake-dispatched', { shakeDuration, shakeIntensity });
    }

    const flashDuration = Math.max(0, Number(effect?.flashDurationMs ?? 0));
    if (!this.reduceFlashes && flashDuration > 0) {
      this.scene.cameras.main.flash(flashDuration, 255, 255, 255);
      this.logger.debug('playCameraEffect:flash-dispatched', { flashDuration });
    }
    return this.reduceShake || this.reduceFlashes;
  }

  private playPlayerTint(
    player: Phaser.Physics.Arcade.Sprite | undefined,
    effect: GameSettingsContent['combatFeedback'][keyof GameSettingsContent['combatFeedback']] | undefined
  ): void {
    if (!player || this.reduceFlashes) {
      this.logger.debug('playPlayerTint:skipped-missing-player');
      return;
    }

    const tintDuration = Math.max(0, Number(effect?.tintDurationMs ?? 0));
    if (tintDuration <= 0) {
      this.logger.debug('playPlayerTint:skipped-zero-duration', { tintDuration });
      return;
    }

    this.clearPlayerTint();
    const tint = { player, colors: [player.tintTopLeft, player.tintTopRight, player.tintBottomLeft, player.tintBottomRight] as [number, number, number, number], timer: undefined as Phaser.Time.TimerEvent | undefined };
    this.playerTint = tint;
    player.getData?.('characterVisual')?.react?.();
    player.setTint(0xff0000);
    tint.timer = this.scene.time.delayedCall(tintDuration, () => this.clearPlayerTint());
    this.logger.debug('playPlayerTint:dispatched', { tintDuration });
  }
}
